import { packVec3 } from "@dust/world/internal";
import IWorldAbi from "@dust/world/out/IWorld.sol/IWorld.abi";
import {
  buildSystemId,
  decodeTransactionError,
  mineSystemId,
} from "@permutation-city/common";
import {
  ActionButton,
  Checkbox,
  Divider,
  NumberRangeSlider,
  TableWithColumns,
  useHotkeys,
} from "@permutation-city/ui";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { usePlayerInventoryQuery } from "../../queries/usePlayerInventoryQuery";
import { useAppStore } from "../../store/appStore";
import { getBestTool } from "../../utils/getBestTool";
import { BlueprintErrorHandler } from "./components/BlueprintErrorHandler";
import { useBlueprintCommon } from "./hooks/useBlueprintCommon";
import { useBlueprintTableData } from "./hooks/useBlueprintTableData";
import { useClosestObjectTypesAt } from "./terrain/useClosestObjectTypesAt";
import { usePreviousNonEmptyArray } from "./usePreviousNonEmptyArray";

export function Terraform() {
  const {
    activeObjectTypeId,
    setAutoSwitch,
    terraformBuildable,
    terraformMineable,
    setTerraformBuildable,
    setTerraformMineable,
    setActiveObjectTypeId,
    matchInventory,
    setMatchInventory,
    terraformSize,
    setTerraformSize,
  } = useAppStore();

  const {
    dustClient,
    userEntityId,
    blueprint,
    isBlueprintLoading,
    handleAddWaypoint,
    isTooFarFromCity,
    isExpandingForceField,
    forceFieldError,
    isEnemyTerritory,
  } = useBlueprintCommon();

  const { data: playerInventory, refetch: refetchPlayerInventory } =
    usePlayerInventoryQuery();

  const {
    blueprintItemsTableData,
    terrainData,
    isLoading: isTerrainObjectTypesLoading,
    refetchTerrainObjectTypes,
  } = useBlueprintTableData(blueprint);
  const { missingBlueprintObjectTypesAt } = terrainData ?? {};

  const closestObjectTypesAt = useClosestObjectTypesAt({
    missingBlueprintObjectTypesAt: missingBlueprintObjectTypesAt ?? [],
  });
  const cachedClosestObjectTypesAt =
    usePreviousNonEmptyArray(closestObjectTypesAt);

  const setBlueprint = useCallback(async () => {
    if (!dustClient || !cachedClosestObjectTypesAt) {
      return;
    }

    await dustClient.provider.request({
      method: "setBlueprint",
      params: {
        blocks:
          cachedClosestObjectTypesAt?.map(
            ({
              objectTypeId,
              position,
              orientation,
            }: {
              objectTypeId: number;
              position: [number, number, number];
              orientation?: number;
            }) => ({
              objectTypeId,
              x: position[0],
              y: position[1],
              z: position[2],
              orientation: orientation ?? 0,
            }),
          ) ?? [],
        options:
          activeObjectTypeId === 1
            ? {
                showBlocksToBuild: false,
                showBlocksToMine: true,
              }
            : undefined,
      },
    });
  }, [dustClient, cachedClosestObjectTypesAt, activeObjectTypeId]);

  const terraformBlocks = useMutation({
    mutationFn: async () => {
      if (!dustClient) return;

      const closestObjectTypeAt = closestObjectTypesAt ?? [];
      const usedInventorySlots = new Map<number, Map<number, number>>();
      const systemCalls = [];

      if (playerInventory) {
        for (const slot of playerInventory) {
          if (!usedInventorySlots.has(slot.objectTypeId)) {
            usedInventorySlots.set(slot.objectTypeId, new Map());
          }
          usedInventorySlots.get(slot.objectTypeId)!.set(slot.slot, 0);
        }
      }

      for (const item of closestObjectTypeAt) {
        let selectedSlot: number | undefined;

        const mineableBlock =
          item.actualObjectTypeId !== 1 &&
          item.objectTypeId !== item.actualObjectTypeId;
        const buildableBlock =
          item.actualObjectTypeId === 1 && item.objectTypeId !== 1;

        if (mineableBlock) {
          const toolSlot = getBestTool(playerInventory, item.objectTypeId);

          if (toolSlot === null) {
            console.log("⚠️ No suitable tool found for mining");
            continue;
          }

          const systemCall = {
            systemId: mineSystemId,
            abi: IWorldAbi,
            functionName: "mineUntilDestroyed",
            args: [userEntityId, packVec3(item.position), toolSlot, "0x"],
          };

          systemCalls.push(systemCall);
        }

        if (buildableBlock) {
          if (activeObjectTypeId === 0) {
            const objectTypeSlots =
              playerInventory?.filter(
                (slot: {
                  objectTypeId: number;
                  slot: number;
                  amount: number;
                }) => slot.objectTypeId === item.objectTypeId,
              ) || [];

            for (const slot of objectTypeSlots) {
              const usedAmount =
                usedInventorySlots.get(item.objectTypeId)?.get(slot.slot) || 0;
              if (usedAmount < slot.amount) {
                selectedSlot = slot.slot;
                usedInventorySlots
                  .get(item.objectTypeId)!
                  .set(slot.slot, usedAmount + 1);
                break;
              }
            }
          } else {
            const inventorySlot = playerInventory?.find(
              (slot: {
                objectTypeId: number;
                slot: number;
                amount: number;
              }) => slot.objectTypeId === activeObjectTypeId,
            );
            selectedSlot = inventorySlot?.slot;
          }

          if (selectedSlot !== undefined) {
            const systemCall = {
              systemId: buildSystemId,
              abi: IWorldAbi,
              functionName: "build",
              args: [userEntityId, packVec3(item.position), selectedSlot, "0x"],
            };

            systemCalls.push(systemCall);
          }
        }
      }

      const terraformRequests = systemCalls.map(async (systemCall) => {
        const terraformResult = await dustClient.provider.request({
          method: "systemCall",
          params: [systemCall],
        });

        const errorMessage = decodeTransactionError(IWorldAbi, terraformResult);
        if (errorMessage) {
          console.log("⚠️ Failed to terraform ⛏️:", errorMessage);
        }
      });

      await Promise.all(terraformRequests);
      await refetchPlayerInventory();
      await refetchTerrainObjectTypes();
    },
  });
  useHotkeys("ctrl + T", () => terraformBlocks.mutate());

  useEffect(() => {
    setBlueprint();
  }, [setBlueprint]);

  return (
    <BlueprintErrorHandler
      isTooFarFromCity={isTooFarFromCity}
      isExpandingForceField={isExpandingForceField}
      forceFieldError={forceFieldError}
      isEnemyTerritory={isEnemyTerritory}
      onAddWaypoint={handleAddWaypoint}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Checkbox
              name="match-inventory"
              checked={matchInventory}
              onChange={() => setMatchInventory(!matchInventory)}
            >
              MATCH INVENTORY
            </Checkbox>
          </div>

          <div className="flex justify-between items-center gap-2">
            <h4 className="uppercase">Blocks:</h4>
            <NumberRangeSlider
              min={1}
              max={30}
              value={terraformSize}
              onChange={(value) => setTerraformSize(value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <Checkbox
                name="build"
                checked={terraformBuildable}
                onChange={() => setTerraformBuildable(!terraformBuildable)}
              >
                BUILD
              </Checkbox>
            </div>

            <div>
              <Checkbox
                name="mine"
                checked={terraformMineable}
                onChange={() => setTerraformMineable(!terraformMineable)}
              >
                MINE
              </Checkbox>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-4">
              {isTerrainObjectTypesLoading && (
                <p className="text-white/70 uppercase leading-normal">
                  Scanning...
                </p>
              )}

              <ActionButton
                hotkey="^+T"
                onClick={async () => {
                  terraformBlocks.mutate();
                }}
                disabled={
                  !closestObjectTypesAt?.length || terraformBlocks.isPending
                }
              >
                {terraformBlocks.isPending ? "Terraforming..." : "Terraform"}
              </ActionButton>
            </div>
          </div>
        </div>

        {terraformBlocks.isError && (
          <p className="uppercase text-danger text-right">
            {terraformBlocks.error?.message || "Something went wrong"}
          </p>
        )}

        <Divider />

        <TableWithColumns
          columns={[
            {
              name: "Name",
              key: "name",
            },
            {
              name: "Pesos",
              key: "reward",
            },
            {
              name: "Rep",
              key: "reputation",
            },
            {
              name: "Progress",
              key: "progress",
            },
          ]}
          activeRowId={activeObjectTypeId}
          onClick={(item) => {
            setActiveObjectTypeId(Number(item.id));
            setAutoSwitch(false);
          }}
          data={blueprintItemsTableData}
          loading={isBlueprintLoading}
          searchable
        />
      </div>
    </BlueprintErrorHandler>
  );
}
